import { SPORE_RING_REST } from '../../models/spore-mother.ts';
import { clip, type Clip } from '../clip.ts';
import { TAU, envelope, inOut, inQuad, outBack, outCubic, outExpo, outQuad, phase, settle, smooth, span } from '../curves.ts';
import { timeline } from '../pose.ts';

/*
 * La Seta Madre (models/spore-mother.ts): she never moves. She breathes ('idle', and 'walk' the same), dips her cap
 * at a hero and puffs ('attack', the puff at 0.5 s), and the rule: 'swell' (3.0 s, the sim's swell) grows her cap
 * to +35 % and her stalk to +12 % at a steady pace while she shakes harder and harder (sample it at swell × 3 s);
 * 'burst' (1.4 s) starts swollen, bursts at 0.12 s (the ring of spores blows out to full by 0.5 s and drifts up
 * and away by 1.22 s) and she sags and recovers.
 *
 * Conventions: +X on the stalk or the cap tips it forward, Z sideways; scale swells them. `ring` is built tiny at
 * the ground: only 'burst' and 'die' scale it up (1 / SPORE_RING_REST).
 */

type B = 'root' | 'stalk' | 'cap' | 'ring';

const RING = 1 / SPORE_RING_REST;
/** Full swell: the cap's total size and the stalk's. */
const CAP = 1.35, STALK = 1.12;

const breathe = (b: Parameters<Clip<B>['pose']>[0], t: number, dur: number): void => {
  const p = phase(t, dur) * TAU;
  b.stalk.rot(0.015 * Math.sin(p), 0, 0.012 * Math.sin(p + 1.3)).scale(1 + 0.012 * Math.sin(p));
  b.cap.rot(0.025 * Math.sin(p + 0.6), 0.03 * Math.sin(p * 0.5 * 2), 0.02 * Math.sin(p + 2)).scale(1 + 0.025 * Math.sin(p + 0.5));
};

const idle = (name = 'idle'): Clip<B> =>
  clip<B>(name, 4.5, true, (b, t) => breathe(b, t, 4.5), (t) => {
    const k = Math.sin(phase(t, 4.5) * TAU);
    return [1 + 0.008 * k, 1 - 0.006 * k, 1 + 0.008 * k];
  });

const attack = (): Clip<B> =>
  clip<B>('attack', 1.2, false, (b, t) => {
    timeline(b, t, [
      [0, {}],
      [0.38, { stalk: [-0.06, 0, 0], cap: [-0.12, 0, 0, 0, 0, 0, 0.96] }, inOut],
      [0.5, { stalk: [0.1, 0, 0], cap: [0.28, 0, 0, 0, 0, 0, 1.08] }, outExpo],
      [0.75, { stalk: [0.06, 0, 0], cap: [0.18, 0, 0, 0, 0, 0, 1.0] }, outQuad],
      [1.2, {}, inOut],
    ]);
  }, (t) => {
    const q = Math.max(0, settle(t - 0.5, 3, 8)) * (t > 0.5 ? 1 : 0);
    return [1 + 0.03 * q, 1 - 0.03 * q, 1 + 0.03 * q];
  }, 'Soplo');

/** The swell: slow and steady to full size, shaking harder the fuller she gets. */
const swell = (): Clip<B> =>
  clip<B>('swell', 3.0, false, (b, t) => {
    const k = span(t, 0, 3.0, (x) => x * (0.6 + 0.4 * x));
    const sh = k * k;
    const st = 1 + (STALK - 1) * k;
    b.stalk.rot(0.04 * sh * Math.sin(t * 37), 0, 0.04 * sh * Math.sin(t * 31 + 1)).scale(st);
    b.cap.rot(-0.08 * k + 0.05 * sh * Math.sin(t * 43 + 2), 0.04 * sh * Math.sin(t * 29), 0.05 * sh * Math.sin(t * 41)).scale((1 + (CAP - 1) * k) / st);
  }, (t) => {
    const k = span(t, 0, 3.0);
    return [1 + 0.05 * k, 1 - 0.03 * k, 1 + 0.05 * k];
  }, 'Se hincha');

/** The burst: from full swell, a last heave, then she blows and the ring of spores rolls out round her. */
const burst = (): Clip<B> =>
  clip<B>('burst', 1.4, false, (b, t) => {
    const heave = span(t, 0, 0.12, outQuad) * (1 - span(t, 0.12, 0.35, outExpo));
    const sag = span(t, 0.12, 0.35, outExpo) * (1 - span(t, 0.5, 1.4, smooth));
    const cap = CAP + 0.1 * heave - (CAP - 1) * span(t, 0.12, 0.3, outExpo) - 0.12 * sag;
    const st = STALK - (STALK - 1) * span(t, 0.12, 0.35, outExpo) - 0.05 * sag;
    b.stalk.rot(0.12 * sag, 0, 0.03 * Math.sin(t * 20) * sag).scale(st);
    b.cap.rot(-0.08 * (1 - span(t, 0.1, 0.3)) + 0.22 * sag, 0, 0.04 * sag).scale(cap / st);
    const out = span(t, 0.1, 0.5, outCubic) * (1 - span(t, 0.95, 1.22, inQuad));
    b.ring.pos(0, 0.5 * span(t, 0.6, 1.22, inQuad), 0).scale(1 + (RING - 1) * Math.max(0.002, out) * (0.6 + 0.4 * span(t, 0.1, 0.9)));
  }, (t) => {
    const q = Math.max(0, settle(t - 0.12, 2.5, 6)) * (t > 0.12 ? 1 : 0);
    return [1 + 0.08 * q, 1 - 0.1 * q, 1 + 0.08 * q];
  }, 'Revienta');

const hit = (): Clip<B> =>
  clip<B>('hit', 0.6, false, (b, t) => {
    const q = settle(t, 3, 7) * span(t, 0, 0.04, outQuad);
    b.stalk.rot(-0.05 * q, 0, 0.03 * q);
    b.cap.rot(-0.12 * q, 0.05 * q, 0.06 * q).scale(1 - 0.04 * q);
  }, (t) => {
    const q = Math.max(0, settle(t, 3, 7));
    return [1 + 0.04 * q, 1 - 0.05 * q, 1 + 0.04 * q];
  });

const stun = (): Clip<B> =>
  clip<B>('stun', 3, true, (b, t) => {
    const p = phase(t, 3) * TAU;
    b.stalk.rot(0.08, 0, 0.05 * Math.sin(p));
    b.cap.rot(0.22 + 0.04 * Math.sin(p * 2), 0.1 * Math.sin(p), 0.1 * Math.cos(p)).scale(0.97);
  });

/** Death (2.6 s): a last puff, then the cap sags, the stalk folds and she slumps over onto her side. */
const die = (): Clip<B> =>
  clip<B>('die', 2.6, false, (b, t) => {
    timeline(b, t, [
      [0, {}],
      [0.3, { stalk: [-0.05, 0, 0, 0, 0, 0, 1.05], cap: [-0.15, 0, 0, 0, 0, 0, 1.12] }, outQuad],
      [0.6, { stalk: [0.05, 0, 0.05, 0, 0, 0, 0.98], cap: [0.15, 0, 0.1, 0, 0, 0, 0.9] }, outExpo],
      [1.6, { stalk: [0.35, 0, 0.45, 0, 0, 0, 0.92], cap: [0.35, 0, 0.55, 0, -0.1, 0, 0.82] }, inQuad],
      [1.85, { stalk: [0.42, 0, 0.6, 0, 0, 0, 0.9], cap: [0.4, 0, 0.7, 0, -0.12, 0, 0.8] }, outBack],
      [2.6, { stalk: [0.42, 0, 0.62, 0, 0, 0, 0.9], cap: [0.42, 0, 0.72, 0, -0.12, 0, 0.78] }, inOut],
    ], { cap: 0.06 });
    const out = envelope(t, 0.3, 0.7, 1.2, 1.7, outCubic);
    b.ring.pos(0, 0.4 * span(t, 0.8, 1.7), 0).scale(1 + (RING * 0.6 - 1) * Math.max(0.003, out));
  }, (t) => {
    const q = Math.max(0, settle(t - 1.6, 2.5, 7)) * (t > 1.6 ? 1 : 0);
    return [1 + 0.05 * q, 1 - 0.06 * q, 1 + 0.05 * q];
  });

/** Clips by library kind (see ../rigs.ts). */
export const sporeMotherClips = (): Clip[] => [idle(), idle('walk'), attack(), hit(), stun(), die(), swell(), burst()] as Clip[];
export const CLIPS: Record<string, () => Clip[]> = { sporeMother: sporeMotherClips };
